/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      route — app-wide root route: session bootstrap, locale setup,
 *              shared popper-positioned dropdown helper
 *   extends:   Route
 *   template:  none
 *   model:     returns { dispos: {contacts:[], products:[]}, me: session.me
 *              or null } — no server fetch of its own
 *   key:       beforeModel (locale + session.bootstrapSession), setupController
 *              (wires urlApi/urlPdfApi/urlSocket/urlFileUpload/urlData,
 *              gdprField, content.open_organization, loginUrl onto the
 *              controller), setPopperTask, willTransition, error action
 *   uses:      store/notify/router/session/intl services; beebee-base/lib/get-config;
 *              @popperjs/core (createPopper)
 *   siblings:  controllers/application.js, templates/application.hbs
 *   tests:     wave5-coverage-test.js, wave16-route-test.js
 *   gotchas:   setPopperTask destroys the previous popper instance before
 *              creating a new one — an overwrite without destroy leaks
 *              scroll/resize listeners that keep repositioning a stale popup.
 *
 * Root route mounted for every page: bootstraps the anonymous session before
 * any child route runs, sets the allowed locales, and exposes shared
 * app-controller wiring (URLs, gdprField, open_organization) plus a
 * Popper.js-backed dropdown-positioning task used by header/menu components.
 */
import Route from '@ember/routing/route';
import { task, timeout } from 'ember-concurrency';
import { notifyUserError, beebeeAjaxGet } from 'beebee-base/lib/utility';
import { set } from '@ember/object';
import { service } from '@ember/service';
import { action } from '@ember/object';

import { createPopper } from '@popperjs/core';

import getConfig from 'beebee-base/lib/get-config';
export default class ApplicationRoute extends Route {
  @service store;
  @service notify;
  @service router;
  @service session;
  @service intl;

  urlApi = getConfig(this).APP.urlApi;
  urlPdfApi = getConfig(this).APP.urlPdfApi;
  urlFileUpload = getConfig(this).APP.urlFileUpload;
  urlData = getConfig(this).APP.urlData;
  urlSocket = getConfig(this).APP.urlSocket;

  async beforeModel(transition) {
    await super.beforeModel(transition);
    this.intl.setLocale(['en-US', 'en-GB', 'it-IT', 'fr-FR', 'de-DE']);

    const controller = this.controllerFor('application');
    this.session.setAnonymous(true);
    await this.session.bootstrapSession(transition, controller);
  }

  model() {
    return {
      dispos: {
        contacts: [],
        products: [],
      },
      me: this.session.isAuthenticated() ? this.session.me : null,
    };
  }

  setupController(controller, model) {
    super.setupController(controller, model);
    //this.session.loadOpen.perform(controller);

    if (controller.get('lang')) {
      controller.switchLangTask.perform(controller.get('lang'));
    }
    controller.setProperties({
      content: model,
      urlApi: this.urlApi,
      urlPdfApi: this.urlPdfApi,
      urlSocket: this.urlSocket,
      urlFileUpload: this.urlFileUpload,
      urlData: this.urlData,
    });

    const { gdprField, openOrganization } = this.session;
    set(controller, 'gdprField', gdprField);
    set(controller, 'content.open_organization', openOrganization);
    set(controller, 'loginUrl', `${window.location.protocol}//${window.location.host}`);
  }

  @task(function* (ref, pop) {
    yield timeout(100);

    const refToggle = ref?.dataset ? ref.dataset.toggle : ref?.attr?.('data-toggle');
    const popId = pop?.id ?? pop?.attr?.('id');
    let refEl = document.querySelector(`[data-toggle="${refToggle}"]`);
    let popEl = document.querySelector(`#${popId}`);

    if (refEl && popEl) {
      document.body.classList.add('dropdown-open');
      // Destroy the previous instance: each overwrite without destroy leaks a
      // popper (scroll/resize listeners) that keeps repositioning the popup.
      this.popper?.destroy?.();

      let popper = createPopper(refEl, popEl, {
        placement: 'bottom-start',
        modifiers: [
          {
            name: 'flip',
            options: {
              fallbackPlacements: ['bottom', 'right', 'top', 'left'],
            },
          },
        ],
      });

      set(this, 'popper', popper);
    }
  })
  setPopperTask;

  formatIncluded(included) {
    const inc = [];
    const incFirst = included[0];

    for (const key in incFirst) {
      if (Object.prototype.hasOwnProperty.call(incFirst, key)) {
        const value = incFirst[key];
        for (const keyV in value) {
          if (Object.prototype.hasOwnProperty.call(value, keyV)) {
            const valueV = value[keyV];
            inc.push(valueV);
          }
        }
      }
    }

    return inc;
  }

  /*@(task(function* (controller) {
    yield timeout(150);

    try {
      let open = null;
      if (typeof globalOrganizationOpen !== 'undefined') {
        open = globalOrganizationOpen;
      }

      if (open) {
        const incFormat = this.formatIncluded(open.included);
        open = {
          data: open.data,
          included: incFormat,
        };
      } else {
        const url = `${getConfig(this).APP.urlApi}/organizations/open`;
        open = yield beebeeAjaxGet(url);
      }

      const field = open?.data?.relationships?.gdpr;
      set(controller, 'gdprField', field);

      this.store.pushPayload(open);

      const openStore = this.store.peekRecord('organization', open.data.id);
      set(controller, 'content.open_organization', openStore);

      var getUrl = window.location;
      set(controller, 'loginUrl', getUrl.protocol + '//' + getUrl.host);
    } catch (e) {
      notifyUserError(this.notify, e, this.intl);
    }
  }).drop())
  loadOpen;*/

  @(task(function* () {
    yield timeout(50);

    document.querySelectorAll('.scroll-top-on-insert').forEach((el) => {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }).restartable())
  animateTask;

  @action
  willTransition(/*transition*/) {
    const applicationController = this.controllerFor('application');

    if (applicationController.windowsWidth <= 640) {
      set(applicationController, 'hideMenu', true);
    }

    set(applicationController, 'showCanvasAccount', false);
    set(applicationController, 'showCanvasNotification', false);

    this.animateTask.perform();
  }

  @action
  error(reason /*, transition*/) {
    const application = this.controllerFor('application');

    if (reason && reason.errors) {
      reason.errors.forEach(function (error) {
        application.notify.error(error.title);
      });
    }

    return true;
  }
}
